import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  AllowanceEntity,
  BokkzAlgDataService,
  BokkzTslDataService,
  BokkzTslLogic,
  ClientEntity,
  Kind,
  OpvangPeriodes
} from "@snelstart-ventures/fiscaal/domain-logic";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {filter, Subject, takeUntil} from "rxjs";
import {Event, NavigationEnd, Router} from "@angular/router";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-kinderen',
  templateUrl: './kinderen.component.html',
  styleUrls: ['./kinderen.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiAlertModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class KinderenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private destroy$ = new Subject<void>();
  private clientEntity ={} as ClientEntity;  private isSaving = false;
  public allowanceEntity = {} as AllowanceEntity;
  public kindFg = {} as FormGroup;
  private focusedElementId: string | null = null;
  public formStructure: any;
  public today: Date = new Date();

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

    public optionsNvt: SelectFormControlItem<boolean>[] = [
        {value: false, label: "N.v.t."},
    ];

  public itemsInschrijvingAangever: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Wel ingeschreven bij aangever"},
    {value: false, label: "Niet ingeschreven bij aangever"}
  ];

  constructor(
    public dataService: BokkzTslDataService,
    private dataAlgService: BokkzAlgDataService,
    private router: Router,
    public tslLogic: BokkzTslLogic,
    private fb: FormBuilder) {
    this.kindFg = this.fb.group({
      kinderen: this.fb.array<Kind>([])
    });
  }

  get kinderen(): FormArray {
    return this.kindFg.get('kinderen') as FormArray;
  }

  addRij(): void {
    this.kinderen.push(this.createFormGroup());
  }

  verwijderRij(index: number): void {
    this.kinderen.removeAt(index);
    this.kindFg.updateValueAndValidity()
    this.kindFg.markAsDirty()
    this.saveForm();
  }

  createFormGroup(kind?: Kind): FormGroup {
    return this.fb.group({
      voornaam: kind ? kind.voornaam : null as unknown as string,
        geboortedatum: [
            kind ? kind.geboortedatum : null as unknown as Date,
            [this.dateNotInFutureValidator(), this.dateNotOlderThan18Validator()]
        ],
      bsn: kind ? kind.bsn : null as unknown as string,
      ingeschrevenOpAdresAangever: kind ? kind.ingeschrevenOpAdresAangever : true,
      ouderOntvangtKinderbijslag: kind ? kind.ouderOntvangtKinderbijslag : true,
      onderhoudBelangrijkeMate: kind ? kind.onderhoudBelangrijkeMate : false,
      opvangPerKind: kind ? [kind.opvangPerKind] : this.fb.array<OpvangPeriodes>([]),
    });
  }

  public ngOnInit(): void {
    this.dataService.allowanceEntity.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.allowanceEntity = value;
      this.updateForm();
    });
    this.dataAlgService.client.pipe(takeUntil(this.destroy$)).subscribe((clientEntity)=> {
      this.clientEntity=clientEntity;
    });
    this.router.events.pipe(
      takeUntil(this.destroy$),
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {this.handleUrlChange()});
  }

  private updateForm(): void {
    const lijst = this.allowanceEntity?.toeslagen?.kinderen || [];
    const formArray = this.kindFg.get('kinderen') as FormArray;
    const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;
    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i]);
      } else {
        const formGroup = this.createFormGroup(lijst[i])
        formArray.push(formGroup);
      }
    }
    if (focusedControl && document.contains(focusedControl)) {
      focusedControl.focus();
    }
  }

  saveForm(): void {
    setTimeout(()=>{
      this.kindFg.updateValueAndValidity();
      this.allowanceEntity.toeslagen = {
        ...this.allowanceEntity.toeslagen,
        kinderen: this.kindFg.value.kinderen
      } as AllowanceEntity
      this.dataService.updateAllowanceTaxEntity(this.allowanceEntity);
    },50)
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleUrlChange() {
    if (!this.isSaving && this.hasUnsavedChanges()) {
      this.isSaving = true;
      this.tslLogic.postAllowances(this.clientEntity.sstRelationId!, this.allowanceEntity ,false);
      this.isSaving = false;
    }
  }

  private hasUnsavedChanges(): boolean {
    return this.kindFg.dirty
  }

    dateNotInFutureValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            const inputDate = new Date(control.value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (inputDate >= today) {
                return { futureDate: "Onjuiste datum" };
            }

            return null;
        };
    }

    dateNotOlderThan18Validator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            const inputDate = new Date(control.value);
            const today = new Date();
            const eighteenYearsAgo = new Date(this.allowanceEntity!.year! - 19, 0, 1); // 1 januari van 18 jaar geleden

            // Fout als geboortedatum VOOR 1 januari van 18 jaar geleden ligt
            if (inputDate < eighteenYearsAgo) {
                return { tooOld: "Datum niet voor " + (this.allowanceEntity!.year! - 19) + "!" };
            }

            return null;
        };
    }

}
